"use client"

import { useEffect, useState } from "react"
import { getPostHogFeatureFlagClient } from "@/shared/lib/monitoring/telemetry-config"

/** PostHog boolean flag that swaps /generate/image to the Studio layout. */
export const GENERATE_STUDIO_FLAG = "generate-studio"

/** Blocked or slow PostHog must not hold the page: fall back to the classic generator. */
const FLAG_RESOLVE_TIMEOUT_MS = 1500

export interface GenerateStudioFlagState {
  enabled: boolean
  loaded: boolean
}

/** Local QA only: `?studio=1` / `?studio=0`. Production builds always ask PostHog. */
function readDevOverride(): boolean | null {
  if (process.env.NODE_ENV === "production") return null
  const value = new URLSearchParams(window.location.search).get("studio")
  if (value === "1") return true
  if (value === "0") return false
  return null
}

/** Reads the generate-studio flag once per mount; falls back to off when PostHog is slow or blocked. */
export function useGenerateStudioFlag(): GenerateStudioFlagState {
  const [state, setState] = useState<GenerateStudioFlagState>({ enabled: false, loaded: false })

  useEffect(() => {
    const override = readDevOverride()
    if (override !== null) {
      setState({ enabled: override, loaded: true })
      return
    }

    const posthog = getPostHogFeatureFlagClient()
    if (!posthog) {
      setState({ enabled: false, loaded: true })
      return
    }

    // Decide once per mount. A late flag load (or identify() reloading flags)
    // must never swap layouts under a user mid-generation.
    let resolved = false
    let unsubscribe: (() => void) | void = undefined
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined

    /** Settles the flag once and stops listening for later flag loads. */
    const resolve = (enabled: boolean) => {
      if (resolved) return
      resolved = true
      clearTimeout(timeout)
      if (typeof unsubscribe === "function") unsubscribe()
      setState({ enabled, loaded: true })
    }

    /** Resolves from the current flag value if PostHog has one. */
    const apply = () => {
      const value = posthog.getFeatureFlag(GENERATE_STUDIO_FLAG)
      if (value !== undefined) resolve(value === true)
    }

    timeout = setTimeout(() => resolve(false), FLAG_RESOLVE_TIMEOUT_MS)
    unsubscribe = posthog.onFeatureFlags?.(apply)
    apply()

    return () => {
      resolved = true
      clearTimeout(timeout)
      if (typeof unsubscribe === "function") unsubscribe()
    }
  }, [])

  return state
}
